import React from 'react';
import { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import '@assets/css/Admin/components/calendar.scss';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import { format, parseISO, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ─── Helpers ────────────────────────────────────────────────────────────────

const lettersName = (name) => {
  if (!name) return '?';
  const arr = name.trim().split(' ').filter(Boolean);
  if (arr.length === 0) return '?';
  const skip = ['de', 'da', 'do', 'das', 'dos'];
  if (arr[1] && skip.includes(arr[1].toLowerCase()) && arr[2]) {
    return arr[0][0].toUpperCase() + arr[2][0].toUpperCase();
  } else if (arr[0] && arr[1]) {
    return arr[0][0].toUpperCase() + arr[1][0].toUpperCase();
  }
  return arr[0][0].toUpperCase();
};

const languages = { pt: 'Português', en: 'Inglês', es: 'Espanhol' };

// ── Cupos-1 (2026-06-04): ordenação por blocos dentro do dia ────────────────
// Blocos: 0 Santiago · 1 Privado · 2 Atacama · 3 Argentina · 100+ Transfer
// (transfer por último, internamente na mesma sequência de regiões).
// Dentro de cada bloco: mais vagas disponíveis primeiro; lotado desce.
const regionGroup = (name = '') => {
  const n = String(name).toLowerCase();
  if (/(atacama|calama|san pedro)/.test(n)) return 2;
  if (/(bariloche|ushuaia|calafate|chalt|buenos aires|argentina|mendoza|el bols)/.test(n)) return 3;
  return 0; // Santiago / default
};
const cupoBlockRank = (obj) => {
  const isTransfer = obj.is_transfer === true || obj.type === 'transfer' || obj.tour_type === 'transfer';
  const label = obj.region_name || obj.tour_name || obj.title || '';
  if (isTransfer) return 100 + regionGroup(label);
  if (obj.type === 'single' || obj.tour_type === 'single') return 1; // Privado
  return regionGroup(label);
};
const cupoFreeSeats = (obj) => Number(obj.availables_cupos || 0);
const cupoSort = (a, b) =>
  cupoBlockRank(a) - cupoBlockRank(b) ||
  cupoFreeSeats(b) - cupoFreeSeats(a) ||
  String(a.tour_name || a.title || '').localeCompare(String(b.tour_name || b.title || ''));

// ─── Mobile: lista agrupada por dia ─────────────────────────────────────────

const MobileListView = ({ reservations, theme, load, getSeats }) => {
  const todayStr = () => {
    const d = new Date();
    const first = d.getDate() - d.getDay();
    const start = new Date(d.setDate(first));
    return start;
  };

  const [weekStart, setWeekStart] = useState(todayStr);
  const loggedUserId = JSON.parse(localStorage.getItem('user'))?.data?.id;

  const prevWeek = () => {
    const prev = subDays(weekStart, 7);
    setWeekStart(prev);
    getSeats(
      '/cupos_general',
      format(prev, 'yyyy-MM-dd'),
      format(addDays(prev, 6), 'yyyy-MM-dd')
    );
  };

  const nextWeek = () => {
    const next = addDays(weekStart, 7);
    setWeekStart(next);
    getSeats(
      '/cupos_general',
      format(next, 'yyyy-MM-dd'),
      format(addDays(next, 6), 'yyyy-MM-dd')
    );
  };

  // Agrupa por data
  const allTours = Object.values(reservations);
  const byDate = {};
  allTours.forEach((obj) => {
    if (!byDate[obj.tour_date]) byDate[obj.tour_date] = [];
    byDate[obj.tour_date].push(obj);
  });
  // Cupos-1: ordena cada dia por bloco (Santiago/Privado/Atacama/Argentina/Transfer) + vagas desc
  Object.keys(byDate).forEach((d) => byDate[d].sort(cupoSort));
  const sortedDates = Object.keys(byDate).sort();

  const weekEnd = addDays(weekStart, 6);
  const weekLabel =
    format(weekStart, "d 'de' MMM", { locale: ptBR }) +
    ' – ' +
    format(weekEnd, "d 'de' MMM", { locale: ptBR });

  return (
    <div style={{ fontFamily: 'Poppins, Arial, sans-serif' }}>
      {/* Navegação de semana */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 0 12px', marginBottom: '4px'
      }}>
        <button onClick={prevWeek} style={navBtnStyle}>‹</button>
        <span style={{ color: '#e8f0fb', fontWeight: 600, fontSize: '14px' }}>{weekLabel}</span>
        <button onClick={nextWeek} style={navBtnStyle}>›</button>
      </div>

      {load && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <CircularProgress size={28} style={{ color: '#38bdf8' }} />
        </div>
      )}

      {!load && sortedDates.length === 0 && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 0', fontSize: '14px' }}>
          Nenhuma reserva nesta semana
        </div>
      )}

      {!load && sortedDates.map((date) => {
        const dayLabel = format(parseISO(date), "EEEE, d 'de' MMMM", { locale: ptBR });
        return (
          <div key={date} style={{ marginBottom: '16px' }}>
            {/* Cabeçalho do dia */}
            <div style={{
              backgroundColor: '#0f2340', borderRadius: '8px 8px 0 0',
              padding: '6px 12px', borderBottom: '2px solid #38bdf8'
            }}>
              <span style={{ color: '#38bdf8', fontWeight: 700, fontSize: '13px', textTransform: 'capitalize' }}>
                {dayLabel}
              </span>
            </div>

            {/* Cards dos passeios do dia */}
            {byDate[date].map((obj, idx) => {
              // Vermelho de "lotado" só em Santiago (region 80). Outras regiões mantêm a cor da região.
              const isSantiago = Number(obj.region_id) === 80;
              const regionC = regionColorFor(obj.region_id);
              const isTransfer = obj.is_transfer === true;
              const isPrivate = !isTransfer && obj.type === 'single';
              // Mesma lógica do getEvents (desktop): um privado (single) do mesmo tour/data
              // consome vaga do grupo — desconta de availables_cupos, senão o mobile mostra
              // "X livres" numa data que na verdade está lotada.
              let availCupos = Number(obj.availables_cupos);
              const tourPrivate = allTours.find(t => t.tour_date === obj.tour_date && t.type === 'single' && t.tour_name === obj.tour_name);
              if (tourPrivate?.tour_date) {
                const totalReservedCupos = tourPrivate.reservations.reduce(
                  (total, item) => item?.reservation_id == tourPrivate?.reservation_id ? total + Number(item.cupos_customer) : total, 0
                );
                availCupos = availCupos - totalReservedCupos;
              }
              let contagemCupos = 0;
              obj.reservations?.forEach(r => {
                const typeMatch = isTransfer
                  ? (r.type === 'transfer' || r.type === 'single')
                  : r.type === obj.type;
                const dateMatch = !r.tour_date || r.tour_date.slice(0, 10) === obj.tour_date?.slice(0, 10);
                if (typeMatch && dateMatch) contagemCupos += parseInt(r.cupos_customer_adults || 0) + parseInt(r.cupos_customer_kids || 0);
              });
              const livres = availCupos > 0
                ? availCupos - contagemCupos
                : 0;
              const lotado = availCupos > 0
                ? livres <= 0
                : contagemCupos > 0;

              const headerBg = isTransfer
                ? '#ffffff'
                : isPrivate
                ? '#3b0764'
                : (lotado && isSantiago)
                ? '#7f1d1d'
                : regionC.bg;
              const headerColor = isTransfer
                ? '#0f172a'
                : isPrivate
                ? '#e9d5ff'
                : (lotado && isSantiago)
                ? '#fca5a5'
                : regionC.text;

              const customers = obj.reservations?.filter(r => r.type === obj.type && r.tour_date === obj.tour_date) || [];

              return (
                <div key={idx} style={{
                  backgroundColor: '#0f2340',
                  borderBottom: idx < byDate[date].length - 1 ? '1px solid #1e3a5f' : 'none',
                  borderRadius: idx === byDate[date].length - 1 ? '0 0 8px 8px' : '0',
                }}>
                  {/* Header do passeio */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    backgroundColor: headerBg, padding: '5px 10px',
                  }}>
                    <span style={{ color: headerColor, fontWeight: 700, fontSize: '12px', textTransform: 'capitalize', flex: 1 }}>
                      {obj.tour_name?.toLowerCase()}
                    </span>
                    {(isTransfer || isPrivate) && contagemCupos > 0 && (
                      <span style={{
                        color: headerColor, fontWeight: 700, fontSize: '12px',
                        backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '12px',
                        padding: '1px 8px', marginLeft: '8px', whiteSpace: 'nowrap'
                      }}>
                        {contagemCupos}
                      </span>
                    )}
                    {!isPrivate && !isTransfer && Number(obj.availables_cupos) > 0 && (
                      <span style={{
                        color: headerColor, fontWeight: 700, fontSize: '12px',
                        backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '12px',
                        padding: '1px 8px', marginLeft: '8px', whiteSpace: 'nowrap'
                      }}>
                        {livres > 0 ? `${livres} livre${livres > 1 ? 's' : ''}` : contagemCupos}
                      </span>
                    )}
                  </div>

                  {/* Clientes */}
                  {customers.map((element, cidx) => (
                    <div key={cidx} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '5px 10px', borderTop: '1px solid #1e3a5f'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                        <span style={{ color: '#94a3b8', fontSize: '11px', whiteSpace: 'nowrap' }}>
                          {element.cupos_customer_adults}
                          {element.cupos_customer_kids > 0 ? `+${element.cupos_customer_kids}` : ''}
                        </span>
                        {theme?.permission === 'operacao' ? (
                          loggedUserId === element.user_id ? (
                            <Link to={`/admin/reservas/detalhe/${element.sale_id}`}
                              style={{ color: '#7dd3fc', fontSize: '12px', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {element.customer_name}
                            </Link>
                          ) : (
                            <span style={{ color: '#e2e8f0', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {element.customer_name}
                            </span>
                          )
                        ) : (
                          <Link to={`/admin/reservas/detalhe/${element.sale_id}`}
                            style={{ color: '#7dd3fc', fontSize: '12px', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {element.customer_name}
                          </Link>
                        )}
                        <span style={languageStyle}>{element.customer_language}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <span style={company_1}>
                          {lettersName(element.seller_name)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {customers.length === 0 && (
                    <div style={{ padding: '6px 10px', color: '#475569', fontSize: '11px', fontStyle: 'italic' }}>
                      Sem reservas
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// ─── Renderizador de eventos (desktop) ──────────────────────────────────────

const renderEventContent = (props) => {
  document.querySelectorAll(".fc-col-header-cell-cushion").forEach(el => {
    el.style.fontSize = '14px'; el.style.fontWeight = '100';
  });

  const lettersNameLocal = lettersName;

  let contagemCupos = 0;
  let typeTravel = [];
  let loggedUserId = JSON.parse(localStorage.getItem('user'))?.data?.id;
  const isAtacama = Number(props.event._def.extendedProps?.region_id) === 79;
  // Vermelho de "lotado" só em Santiago (region 80). Outras regiões mantêm a cor da região mesmo lotadas.
  const isSantiago = Number(props.event._def.extendedProps?.region_id) === 80;
  const regionId = Number(props.event._def.extendedProps?.region_id);

  if (props.event._def.extendedProps?.tour_type === 'transfer') {
    return (
      <>
        {props.event._def.extendedProps.cupos.map((element) =>
          (element.type === 'transfer' || element.type === 'single') && (
            <div style={cardStyles} key={element.sale_id}>
              <div className="text-blue font-poppins">
                <p style={titleStylesTransfer}>
                  <span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span>
                  <span>{Number(element.cupos_customer_adults || 0) + Number(element.cupos_customer_kids || 0)}</span>
                </p>
                <div style={itensStyles}>
                  <div className='flex'>
                    <span>
                      {element.cupos_customer_adults}{element.cupos_customer_kids > 0 ? '+' + element.cupos_customer_kids : ''}-
                      {props.event._def.extendedProps?.permission === 'operacao' ?
                        loggedUserId === element.user_id ?
                          <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                          : <span>{element.customer_name}</span>
                        :
                        <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                      }
                    </span>
                    <Tooltip title={languages[element.customer_language]} className='cursor-pointer'>
                      <span style={language}>{element.customer_language}</span>
                    </Tooltip>
                  </div>
                  <div className='flex cursor-pointer'>
                    <Tooltip title={element.seller_name}>
                      <span style={company_1}>{lettersNameLocal(element.seller_name)}</span>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </>
    );
  }

  if (props.event._def.extendedProps?.tour_type !== 'single') {
    return (
      <div style={cardStyles}>
        <div className="text-blue font-poppins">
          {Number(props.event._def.extendedProps?.availables_cupos) > 0 ? (
            <>
              {props.event._def.extendedProps.cupos.map((element) => {
                contagemCupos += parseInt(element.cupos_customer);
                if (!typeTravel.includes(element.type)) typeTravel.push(element.type);
              })}
              {typeTravel.includes(props.event._def.extendedProps?.tour_type) && props.event._def.extendedProps?.tour_type !== 'single' ? (
                <>
                  {Number(props.event._def.extendedProps.availables_cupos) - Number(contagemCupos) > 0 ?
                    // Há vaga: mostra o LIVRE (capacidade − reservado)
                    <p style={regionTitleStyle(regionId)}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span> <span>{Number(props.event._def.extendedProps.availables_cupos) - Number(contagemCupos)}</span></p>
                    :
                    // Esgotado (livre ≤ 0): mostra o total de PAX no tour; vermelho só em Santiago
                    <p style={isSantiago ? titleStyles2 : regionTitleStyle(regionId)}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span> <span className='text-white'>{Number(contagemCupos)}</span></p>
                  }
                </>
              ) : (
                <>
                  {Number(props.event._def.extendedProps.availables_cupos) - Number(contagemCupos) === 0 ?
                    <p style={isSantiago ? titleStyles2 : regionTitleStyle(regionId)}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span> <span className='text-white'>{Number(props.event._def.extendedProps.availables_cupos)}</span></p>
                    :
                    <p style={isAtacama ? titleStylesAtacama : titleStyles3}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span></p>
                  }
                </>
              )}
            </>
          ) : (
            <>
              {props.event._def.extendedProps.cupos.map((element) => {
                contagemCupos += parseInt(element.cupos_customer);
              })}
              <p style={isSantiago ? titleStyles2 : regionTitleStyle(regionId)}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span> <span className='text-white'>{props.event._def.extendedProps.availables_cupos + contagemCupos}</span></p>
            </>
          )}
          {props.event._def.extendedProps.cupos.map((element) => {
            if (element.type === props.event._def.extendedProps.tour_type)
              return (
                <div style={itensStyles} key={element.sale_id}>
                  <div className='flex'>
                    <span>
                      {element.cupos_customer_adults}{element.cupos_customer_kids > 0 ? '+' + element.cupos_customer_kids : ''}-
                      {props.event._def.extendedProps?.permission === 'operacao' ?
                        loggedUserId === element.user_id ?
                          <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                          : <span>{element.customer_name}</span>
                        :
                        <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                      }
                    </span>
                    <Tooltip title={languages[element.customer_language]} className='cursor-pointer'>
                      <span style={language}>{element.customer_language}</span>
                    </Tooltip>
                  </div>
                  <div className='flex cursor-pointer'>
                    <Tooltip title={element.seller_name}>
                      <span style={company_1}>{lettersNameLocal(element.seller_name)}</span>
                    </Tooltip>
                  </div>
                </div>
              );
          })}
        </div>
      </div>
    );
  }

  if (props.event._def.extendedProps?.tour_type === 'single') {
    return (
      <>
        {props.event._def.extendedProps.cupos.map((element) =>
          element.type === props.event._def.extendedProps.tour_type && (
            <div style={cardStyles} key={element.sale_id}>
              <div className="text-blue font-poppins">
                <p style={titleStyles3}><span>{props.event._def.extendedProps?.tour_name.toLowerCase()}</span><span>{Number(element.cupos_customer_adults || 0) + Number(element.cupos_customer_kids || 0)}</span></p>
                <div style={itensStyles}>
                  <div className='flex'>
                    <span>
                      {element.cupos_customer_adults}{element.cupos_customer_kids > 0 ? '+' + element.cupos_customer_kids : ''}-
                      {props.event._def.extendedProps?.permission === 'operacao' ?
                        loggedUserId === element.user_id ?
                          <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                          : <span>{element.customer_name}</span>
                        :
                        <Tooltip title="Ver reserva"><Link color="inherit" to={`/admin/reservas/detalhe/${element.sale_id}`}>{element.customer_name}</Link></Tooltip>
                      }
                    </span>
                    <Tooltip title={languages[element.customer_language]} className='cursor-pointer'>
                      <span style={language}>{element.customer_language}</span>
                    </Tooltip>
                  </div>
                  <div className='flex cursor-pointer'>
                    <Tooltip title={element.seller_name}>
                      <span style={company_1}>{lettersNameLocal(element.seller_name)}</span>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </>
    );
  }
};

// ─── Componente principal ────────────────────────────────────────────────────

const Calendar = (props) => {
  const windowSize = useRef([window.innerWidth, window.innerHeight]);
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const isMobile = windowSize.current[0] < 768;

  const getEvents = () => {
    const toursArray = Object.values(props.reservations);
    return toursArray.map((obj) => {
      let tours = [];
      obj.reservations.forEach(t => {
        if (t.tour_date === obj.tour_date && t.type === obj.type) tours.push(t);
      });
      var availables_cupos = obj.availables_cupos;
      const tourPrivate = toursArray.find(t => t.tour_date === obj.tour_date && t.type === 'single' && t.tour_name === obj.tour_name);
      if (tourPrivate?.tour_date) {
        const totalReservedCupos = tourPrivate.reservations.reduce(
          (total, item) => item?.reservation_id == tourPrivate?.reservation_id ? total + Number(item.cupos_customer) : total, 0
        );
        availables_cupos = availables_cupos - totalReservedCupos;
      }
      const cuposLabel = availables_cupos > 0 ? ` (${availables_cupos} cupos)` : ' (lotado)';
      return {
        date: obj.tour_date,
        title: obj.tour_name + cuposLabel,
        tour_name: obj.tour_name,
        availables_cupos,
        cupos: tours,
        tour_type: obj.type,
        sale_id: obj.sale_id,
        user_id: obj.user_id,
        region_id: obj.region_id,
        region_name: obj.region_name,
        is_transfer: obj.is_transfer === true,
        sort_rank: cupoBlockRank(obj),
        sort_free: Number(availables_cupos || 0),
        permission: props.theme.permission,
      };
    });
  };

  const calendarRef = React.createRef();

  const refreshCalendar = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().refetchEvents();
      getEvents();
    }
  };

  const changeDates = () => {
    let data = new Date();
    let primeiro = data.getDate() - data.getDay();
    let dataInicial = new Date(data.setDate(primeiro)).toUTCString();
    dataInicial = data.getFullYear() + "-" + (data.getMonth() + 1) + "-" + data.getDate();
    let dataFinal = new Date(data.setDate(data.getDate() + 6)).toUTCString();
    dataFinal = data.getFullYear() + "-" + (data.getMonth() + 1) + "-" + data.getDate();
    setDateStart(dataInicial);
    setDateEnd(dataFinal);
  };

  useEffect(() => {
    document.querySelectorAll(".fc-col-header-cell-cushion").forEach(el => { el.style.fontSize = '14px'; el.style.fontWeight = '100'; });
    document.querySelectorAll(".fc-theme-standard").forEach(el => { el.style.minHeight = ''; el.style.minWidth = ''; });
    if (windowSize.current[0] < 600) {
      document.querySelectorAll(".fc-header-toolbar").forEach(el => { el.style.width = 'max-content'; });
    }
    document.querySelectorAll(".fc-view-harness").forEach(el => { el.style.minHeight = ''; });
    document.querySelectorAll(".fc-view-harness-active").forEach(el => { el.style.minHeight = ''; el.style.minWidth = ''; });
    document.querySelectorAll(".fc-daygrid").forEach(el => { el.style.height = '100%'; });
    document.querySelectorAll(".fc-view-container").forEach(el => { el.style.overflowX = 'auto'; });

    var $hd = document.querySelector(".fc-header-toolbar");
    if ($hd && !document.querySelector(".fc-header-days")) {
      let diasSemana = '<div class="fc-header-days"><span></span><span>Dom</span><span></span><span>Seg</span><span> </span><span>Ter</span><span> </span><span>Qua</span><span> </span><span>Qui</span><span> </span><span>Sex</span><span> </span><span>Sab</span><span></span></div>';
      $hd.insertAdjacentHTML('afterend', diasSemana);
    }
    changeDates();
  }, []);

  useEffect(() => { getEvents(); }, [props.reservations]);
  useEffect(() => { refreshCalendar(); }, [props.load]);

  // ── Mobile: lista por dia ──
  if (isMobile) {
    return (
      <MobileListView
        reservations={props.reservations}
        theme={props.theme}
        load={props.load}
        getSeats={props.getSeats}
      />
    );
  }

  // ── Desktop: calendário semanal ──
  return (
    <div className="flex-1 text-center">
      {props?.load && (
        <div style={loadingStyle}>
          <CircularProgress size={30} />
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        locale="pt-br"
        timeZone="local"
        events={getEvents()}
        eventColor="transparent"
        customButtons={{
          prev: {
            click: function () {
              let calendarApi = calendarRef.current;
              let data = new Date(calendarApi.getApi().getDate().toUTCString());
              let dataF = new Date(calendarApi.getApi().getDate().toUTCString());
              data.setDate(data.getDate() - 7);
              dataF.setDate(dataF.getDate() - 1);
              let dataInicial = data.getFullYear() + "-" + (data.getMonth() + 1) + "-" + data.getDate();
              let dataFinal = dataF.getFullYear() + "-" + (dataF.getMonth() + 1) + "-" + dataF.getDate();
              setDateStart(dataInicial);
              setDateEnd(dataFinal);
              props.getSeats('/cupos_general', dataInicial, dataFinal);
              calendarApi.getApi().prev();
              calendarApi.getApi().refetchEvents();
            },
          },
          next: {
            click: function () {
              let calendarApi = calendarRef.current;
              let data = new Date(calendarApi.getApi().getDate().toUTCString());
              let dataF = new Date(calendarApi.getApi().getDate().toUTCString());
              data.setDate(data.getDate());
              dataF.setDate(dataF.getDate() + 13);
              let dataInicial = data.getFullYear() + "-" + (data.getMonth() + 1) + "-" + data.getDate();
              let dataFinal = dataF.getFullYear() + "-" + (dataF.getMonth() + 1) + "-" + dataF.getDate();
              setDateStart(dataInicial);
              setDateEnd(dataFinal);
              props.getSeats('/cupos_general', dataInicial, dataFinal);
              calendarApi.getApi().next();
              calendarApi.getApi().refetchEvents();
            },
          },
        }}
        headerToolbar={{ start: 'prev', center: 'title', end: 'next' }}
        eventOrder={(a, b) => {
          const rank = (e) => Number(e.extendedProps?.sort_rank ?? e.sort_rank ?? 0);
          const free = (e) => Number(e.extendedProps?.sort_free ?? e.sort_free ?? 0);
          return rank(a) - rank(b) || free(b) - free(a) || String(a.title || '').localeCompare(String(b.title || ''));
        }}
        eventOrderStrict={true}
        dayHeaderFormat={{ day: 'numeric', omitCommas: true }}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridWeek"
        contentHeight="auto"
        titleFormat={{ month: 'long', year: 'numeric' }}
        eventContent={renderEventContent}
      />
    </div>
  );
};

export default Calendar;

// ─── Estilos ────────────────────────────────────────────────────────────────

const navBtnStyle = {
  background: '#1e3a5a',
  border: '1px solid #2a4f72',
  color: '#38bdf8',
  borderRadius: '8px',
  padding: '4px 16px',
  fontSize: '20px',
  cursor: 'pointer',
  lineHeight: 1,
};

const cardStyles = {
  marginBottom: '6px',
  padding: '2px',
  fontSize: '12px',
  backgroundColor: '#0f2340',
  color: '#e2e8f0',
  whiteSpace: 'break-spaces',
  borderRadius: '8px 8px 0px 0px',
  borderBottom: '1px solid #1e3a5f',
};

const titleStylesAtacama = {
  display: 'flex', justifyContent: 'space-between',
  borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
  textTransform: 'capitalize', backgroundColor: '#92400e',
  color: '#fde68a', fontWeight: '700',
};

const titleStyles1 = {
  display: 'flex', justifyContent: 'space-between',
  borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
  textTransform: 'capitalize', backgroundColor: '#0e7490',
  color: '#fff', fontWeight: '700',
};

const titleStyles2 = {
  display: 'flex', justifyContent: 'space-between',
  borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
  textTransform: 'capitalize', backgroundColor: '#7f1d1d',
  color: '#fca5a5',
};

const titleStyles3 = {
  display: 'flex', justifyContent: 'space-between',
  borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
  textTransform: 'capitalize', backgroundColor: '#3b0764',
  color: '#e9d5ff', fontWeight: '700',
};

const titleStylesTransfer = {
  display: 'flex', justifyContent: 'space-between',
  borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
  textTransform: 'capitalize', backgroundColor: '#ffffff',
  color: '#0f172a', fontWeight: '700',
};

// Cor própria por região (region_id). Santiago (80) e Atacama (79) mantidas;
// demais regiões ganharam cor distinta para não ficarem todas iguais ao teal.
// O vermelho de "lotado" (titleStyles2) continua exclusivo de Santiago.
const REGION_COLORS = {
  79: { bg: '#92400e', text: '#fde68a' }, // Atacama
  80: { bg: '#0e7490', text: '#ffffff' }, // Santiago
  81: { bg: '#1d4ed8', text: '#dbeafe' }, // Buenos Aires
  82: { bg: '#15803d', text: '#bbf7d0' }, // Peru
  83: { bg: '#4338ca', text: '#c7d2fe' }, // Ushuaia
  84: { bg: '#be185d', text: '#fbcfe8' }, // Bariloche
  85: { bg: '#475569', text: '#cbd5e1' }, // Calafate
  86: { bg: '#65a30d', text: '#ecfccb' }, // Rio
};
const regionColorFor = (region_id) => REGION_COLORS[Number(region_id)] || { bg: '#0e7490', text: '#ffffff' };
const regionTitleStyle = (region_id) => {
  const c = regionColorFor(region_id);
  return {
    display: 'flex', justifyContent: 'space-between',
    borderRadius: '8px 8px 0px 0px', padding: '2px 5px',
    textTransform: 'capitalize', backgroundColor: c.bg,
    color: c.text, fontWeight: '700',
  };
};

const itensStyles = {
  display: 'flex', padding: '2px 5px', justifyContent: 'space-between',
};

const company_1 = {
  display: 'flex', padding: '3px', backgroundColor: '#134e35',
  color: '#4ade80', borderRadius: '5px', width: '35px',
  justifyContent: 'center', marginInlineEnd: '2px',
  fontSize: '11px', fontWeight: '600',
};

const company_2 = {
  display: 'flex', padding: '3px', backgroundColor: '#2d1b69',
  color: '#c4b5fd', borderRadius: '5px', width: '35px',
  justifyContent: 'center', marginInlineEnd: '2px',
  fontSize: '11px', fontWeight: '600',
};

const language = {
  display: 'flex', padding: '1px', backgroundColor: '#1e3a5f',
  borderRadius: '50px', width: '20px', height: '20px',
  fontSize: '10px', color: '#7dd3fc', justifyContent: 'center',
  alignItems: 'center', fontWeight: '600', textTransform: 'uppercase',
};

const languageStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: '#1e3a5f', borderRadius: '50%',
  width: '18px', height: '18px', fontSize: '9px',
  color: '#7dd3fc', fontWeight: '600', textTransform: 'uppercase',
  flexShrink: 0,
};

const loadingStyle = {
  zIndex: '30000', position: 'relative',
  top: '200px', marginLeft: 'auto', marginRight: 'auto',
};
